import {
  FilesContext,
  FormPanel,
  FormTextArea,
  FormFileTable,
  TableFieldColumn,
  TableFieldCellProps,
  TableFieldContext,
  FileRef,
  TableFieldCells,
  TableFieldToolbarButton,
  FormContext,
  LocaleProvider,
  LocaleName,
  useEventCallback,
  FileTableActionCell,
} from '@eas/common-web';
import RemoveIcon from '@material-ui/icons/Delete';
import GetAppIcon from '@material-ui/icons/GetApp';
import React, { useContext, useState, useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import { HelpLabel } from '../../../components/help/help-label';
import { useIntl } from 'react-intl';
import { StepSubjectForm } from './step-subject-types';
import { HelpBlock2 } from '../step-agenda/step-agenda-content';
import { ProcessForm } from '../uform-types';
import { UFormGovService, UFormAttachment } from '../../../models';
import { getServiceById } from '../../uform-gov-service/uform-gov-service-api';

function AttachmentActionCell(props: TableFieldCellProps<UFormAttachment>) {
  const { getFileUrl } = useContext(FilesContext);
  const { disabled, showRemoveDialog } = useContext(TableFieldContext);

  const handleRemove = useEventCallback(() => {
    showRemoveDialog(props.index);
  });

  return (
    <>
      <TableFieldToolbarButton
        title={
          <FormattedMessage
            id="EAS_FILE_TABLE_BTN_DOWNLOAD"
            defaultMessage="Stáhnout"
          />
        }
        IconComponent={GetAppIcon}
        disabled={false}
        show={true}
        href={getFileUrl(props.rowValue?.file?.id ?? '')}
      />
      <TableFieldToolbarButton
        title={
          <FormattedMessage
            id="EAS_FILE_TABLE_BTN_REMOVE"
            defaultMessage="Smazat"
          />
        }
        IconComponent={RemoveIcon}
        disabled={disabled}
        show={true}
        onClick={handleRemove}
      />
    </>
  );
}

export function useAttachmentColumns(
  isNewForm: boolean
): TableFieldColumn<FileRef>[] {
  const intl = useIntl();

  const columns: TableFieldColumn<FileRef>[] = [
    {
      name: intl.formatMessage({
        id: 'EAS_FILE_TABLE_COLUMN_ACTIONS',
        defaultMessage: 'Akce',
      }),
      datakey: 'id',
      width: 100,
      CellComponent: isNewForm
        ? FileTableActionCell
        : (AttachmentActionCell as any),
    },
    {
      name: intl.formatMessage({
        id: 'EAS_FILE_TABLE_COLUMN_ORDER',
        defaultMessage: 'Číslo',
      }),
      datakey: 'order',
      width: 100,
      CellComponent: TableFieldCells.TextCell,
    },
    {
      name: intl.formatMessage({
        id: 'EAS_FILE_TABLE_COLUMN_NAME',
        defaultMessage: 'Název',
      }),
      datakey: 'file.name',
      width: 400,
      CellComponent: function FileNameCell({ rowValue, ...props }) {
        return (
          <TableFieldCells.TextCell
            {...props}
            rowValue={rowValue}
            value={`${rowValue.name || (rowValue as any)?.file?.name}`}
          />
        );
      },
    },
    {
      name: intl.formatMessage({
        id: 'EAS_FILE_TABLE_COLUMN_CREATED',
        defaultMessage: 'Nahráno',
      }),
      datakey: 'created',
      width: 200,
      CellComponent: TableFieldCells.DateTimeCell,
    },
  ];

  return columns;
}

export function StepSubjectContent({ allValues }: { allValues: ProcessForm }) {
  const { setFieldValue } = useContext<FormContext<StepSubjectForm>>(
    FormContext
  );

  const columns = useAttachmentColumns(true);

  const [serviceDetail, setService] = useState<UFormGovService | null>(null);

  useEffect(() => {
    if (allValues.service) {
      getServiceById(allValues.service.id)
        .json()
        .then((service) => setService(service as UFormGovService));
    } else {
      setService(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allValues.service?.id]);

  return (
    <>
      {serviceDetail?.help && <HelpBlock2 content={serviceDetail?.help} />}
      <FormPanel label="Podání">
        <FormTextArea
          name="submission.text"
          label={
            <HelpLabel
              label="Popis žádosti"
              code="DICT_FORM_APPLICATION_DESCRIPTION"
              required
            />
          }
          labelOptions={{
            hide: true,
          }}
          minRows={8}
        />
      </FormPanel>
      <FormPanel label="Přílohy">
        <LocaleProvider
          defaultLocale={LocaleName.cs}
          messages={{
            CZECH: {
              EAS_FILE_TABLE_MSG_ERROR_UPLOAD_COUNT:
                'Maximální dovolený počet přiložených souborů je 10.',
            },
          }}
        >
          <FormFileTable
            name="submission.attachments"
            label="Přílohy"
            labelOptions={{ hide: true }}
            maxItems={10}
            notifyChange={(value) => {
              const files = (value ?? []) as FileRef[];
              files.forEach((_, index) => {
                setFieldValue(
                  `submission.attachments[${index}].order`,
                  index + 1
                );
              });
            }}
            columns={columns}
          />
        </LocaleProvider>
      </FormPanel>
    </>
  );
}
